/**
 * Warp-field configuration types.
 *
 * A `WarpFieldConfig` is *opt-in* per preset. Presets that don't supply
 * one keep the procedural warp paths from Phase 1 unchanged. Presets that
 * supply one set their `warp.kind` to `Field` so the shader knows to
 * sample the field texture instead of running procedural displacement
 * maths.
 *
 * The runtime system maintains two `rgba16float` displacement textures
 * (RG carries displacement; BA reserved for future metadata such as
 * weight or noise channels). On a beat that meets the adoption gate, it
 * crossfades from the previous active field to a freshly-generated
 * staging field over `crossfadeMs`.
 */

export const WARP_FIELD_GENERATORS = [
  'curl',     // divergence-free curl noise -> smooth swirling flow
  'spiral',   // radial-tangential spiral with adjustable arms
  'swirl',    // pure tangential rotation around an off-centre point
  'julia',    // Julia-set escape field; c walks the Mandelbrot boundary
] as const;
export type WarpFieldGeneratorKind = (typeof WARP_FIELD_GENERATORS)[number];

export type WarpFieldAdoption = {
  /** Minimum beat confidence required to trigger an adoption. */
  beatThreshold: number;
  /** Lockout window between successful adoptions, ms. */
  cooldownMs: number;
  /** Length of the smooth crossfade between previous and new field, ms. */
  crossfadeMs: number;
  /**
   * If true, after each adoption the system regenerates the (now-stale)
   * staging texture with a fresh seed so the next adoption produces a new
   * field rather than swapping back to the previous one. Defaults to true.
   */
  regenerateOnAdopt?: boolean;
};

export type WarpFieldConfig = {
  generator: WarpFieldGeneratorKind;
  /** Initial seed; the engine adds a runtime offset per regeneration. */
  seed: number;
  /** Multiplied into the displacement vector at sample time. */
  strength: number;
  /** Centre of the generator pattern in normalised UV (0..1). */
  centerX?: number;
  centerY?: number;
  /** Generator-specific knobs. Each generator interprets the slots it needs. */
  param0?: number;
  param1?: number;
  param2?: number;
  param3?: number;
  adoption: WarpFieldAdoption;
};
